import { Badge, Button, Checkbox, Chip, createTheme, Divider, Indicator, Notification, Slider, Title } from '@mantine/core'

import { cn } from '@/shared/lib/cn'
import { MEDIA_QUERY } from '@/shared/lib/media-query'

export const mantineTheme = createTheme({
  breakpoints: {
    md: MEDIA_QUERY.desktop,
    lg: MEDIA_QUERY.maxLg,
  },
  fontFamily: 'var(--font-family-main)',
  fontSizes: {
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '18px',
    xl: '20px',
  },
  lineHeights: {
    xs: '15px',
    sm: '20px',
    md: '22px',
    lg: '24px',
    xl: '26px',
  },
  headings: {
    fontWeight: '600',
    fontFamily: 'var(--font-family-main)',
    sizes: {
      h1: {
        fontSize: '40px',
        lineHeight: '46px',
      },
      h2: {
        fontSize: '32px',
        lineHeight: '40px',
      },
      h3: {
        fontSize: '24px',
        lineHeight: '38px',
      },
      h4: {
        fontSize: '20px',
        lineHeight: '24px',
      },
      h5: {
        fontSize: '18px',
        lineHeight: '24px',
      },
      h6: {
        fontSize: '16px',
        lineHeight: '20px',
      },
    },
  },
  components: {
    Badge: Badge.extend({
      classNames: {
        label: cn.asIs('normal-case'),
      },
    }),
    Button: Button.extend({
      classNames: {
        root: (() => {
          const primaryVariant = cn.asIs(
            'bg-brand text-primary-button-label hover:bg-brand-fill-pressed active:bg-brand-fill-hover',
          )

          const whiteVariant = cn.asIs(
            'data-[variant=white]:bg-white data-[variant=white]:text-primary-text data-[variant=white]:hover:bg-neutral-fill-text-dividers-stroke-disabled data-[variant=white]:active:bg-neutral-fill-background-secondary',
          )

          const outlineVariant = cn.asIs(
            'data-[variant=outline]:border data-[variant=outline]:border-neutral-fill-text-dividers-stroke-disabled data-[variant=outline]:bg-white data-[variant=outline]:text-primary-text data-[variant=outline]:hover:bg-neutral-fill-text-dividers-stroke-disabled data-[variant=outline]:active:bg-neutral-fill-background-secondary',
          )

          const transparentVariant = cn.asIs(
            'data-[variant=transparent]:bg-transparent data-[variant=transparent]:text-primary-text data-[variant=transparent]:hover:bg-primary-text data-[variant=transparent]:hover:bg-opacity-5 data-[variant=transparent]:active:bg-primary-text data-[variant=transparent]:active:bg-opacity-10',
          )

          const disabled = cn.asIs(
            'data-[disabled]:pointer-events-none data-[disabled]:!bg-neutral-fill-text-dividers-stroke-disabled data-[disabled]:!text-neutral-text-icons-disabled',
          )

          return cn(
            'rounded-lg px-[12px] py-[11px] text-[16px]/[22px] transition-colors',
            primaryVariant,
            whiteVariant,
            outlineVariant,
            transparentVariant,
            disabled,
          )
        })(),
        label: cn.asIs('desktop:overflow-visible'),
      },
    }),
    Checkbox: Checkbox.extend({
      classNames: {
        input: cn.asIs('border-neutral-fill-text-dividers-stroke-disabled checked:border-brand checked:bg-brand'),
        label: cn.asIs('font-medium text-primary-text'),
      },
    }),
    CheckboxCard: Checkbox.Card.extend({
      defaultProps: {
        withBorder: false,
      },
    }),
    Indicator: Indicator.extend({
      classNames: {
        indicator: cn.asIs('before:bg-brand'),
      },
    }),
    Slider: Slider.extend({
      classNames: {
        trackContainer: cn.asIs('h-[3px]'),
        track: cn.asIs('h-[3px] bg-brand-stroke-primary before:bg-brand-stroke-primary'),
        bar: cn.asIs('bg-brand'),
        label: cn.asIs('bottom-[auto] top-[14px] rounded-[30px] bg-brand px-[8px] py-[4px] font-semibold text-white'),
        thumb: cn.asIs(
          'box-content h-[4px] w-[4px] border-[6px] border-white bg-brand shadow-xl outline outline-1 outline-offset-0 outline-neutral-fill-background-secondary',
        ),
      },
    }),
    Chip: Chip.extend({
      classNames: {
        root: cn.asIs('[--chip-bg:var(--sn-color-brand)]'),
      },
    }),
    Title: Title.extend({
      classNames: {
        root: cn.asIs('text-primary-text'),
      },
    }),
    Divider: Divider.extend({
      classNames: {
        root: cn.asIs('border-neutral-fill-background-secondary'),
      },
    }),
    Notification: Notification.extend({
      classNames: {
        icon: cn.asIs('bg-transparent'),
        root: cn.asIs('rounded-lg shadow-lg first:mt-[60px] desktop:ml-[-125px] desktop:w-[710px]'),
        body: cn.asIs('py-1 pr-1'),
      },

      // Currently `closeButton` class can not be target, we'll have to resort to styles until they fix it.
      styles: {
        closeButton: {
          borderRadius: '50%',
          border: 'rgb(229 231 235) 1px solid',
        },
      },
    }),
  },
})
